import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import supabase from '../config/supabaseClient';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaArrowLeft } from 'react-icons/fa';

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showResendOption, setShowResendOption] = useState(false);

  // Check for error or message in URL parameters
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');
    
    if (errorParam) {
      // Decode and format error messages
      let errorMessage = decodeURIComponent(errorParam);
      
      // Handle specific error codes
      if (errorMessage === 'email_link_expired' || errorMessage.includes('expired') || errorMessage.includes('Email link is invalid or has expired')) {
        errorMessage = 'The email confirmation link has expired. Please request a new confirmation email or sign in if you\'ve already verified your email.';
        setShowResendOption(true);
      } else if (errorMessage === 'verification_failed') {
        errorMessage = 'Email verification failed. Please try again or contact support if the problem persists.';
        setShowResendOption(true);
      }
      
      setError(errorMessage);
      
      // Clean up URL by removing error parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    } else if (messageParam === 'email_verified') {
      setError(''); // Clear any previous errors
      setSuccess('Email verified successfully! You can now sign in.');
      // Could show a success message here if needed
    }
  }, [searchParams, navigate]);

  const handleResendConfirmation = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (resendError) {
        setError(resendError.message || 'Failed to resend confirmation email. Please try again.');
        return;
      }

      setSuccess('Confirmation email sent! Please check your inbox and spam folder.');
      setShowResendOption(false);
    } catch (err) {
      console.error('Resend confirmation error:', err);
      setError('Failed to resend confirmation email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password, {
        options: {
          persistSession: rememberMe
        }
      });
      
      if (error) {
        if (error.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else {
          setError(error.message || 'Failed to sign in');
        }
        return;
      }

      // Sign in successful, navigate to Landing page
      navigate('/');
    } catch (err) {
      console.error('Sign in error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Link to="/" className="back-to-home">
          <FaArrowLeft /> Back to Home
        </Link>

        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img 
            src="/logo.png" 
            alt="Pre Go Prep" 
            style={{ 
              height: '80px'
            }}
          />
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="auth-error">{error}</div>}
          {success && <div style={{ 
            padding: '12px', 
            marginBottom: '16px', 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            border: '1px solid #c3e6cb', 
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}>{success}</div>}
          
          {showResendOption && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeeba',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}>
              <p style={{ margin: '0 0 10px 0', color: '#856404' }}>
                Need a new confirmation email?
              </p>
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resendLoading || !email}
                style={{
                  backgroundColor: '#856404',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: resendLoading || !email ? 'not-allowed' : 'pointer',
                  opacity: resendLoading || !email ? 0.7 : 1,
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                {resendLoading ? 'Sending...' : 'Resend Confirmation Email'}
              </button>
            </div>
          )}

          <div className="form-group">
            <div className="input-icon-wrapper">
              <FaEnvelope className="input-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                required
                className="auth-input"
              />
            </div>
          </div>

          <div className="form-group">
            <div className="input-icon-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="auth-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="rememberMe" style={{ color: '#6c757d', fontSize: '0.9rem' }}>
              Remember me
            </label>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <p style={{
            fontSize: '0.9rem',
            color: '#6c757d',
            textAlign: 'center',
            marginTop: '1rem'
          }}>
            By clicking Sign In you agree to the Pre Go Prep{' '}
            <Link to="/terms-of-use" style={{ color: '#0d6efd', textDecoration: 'none' }}>
              terms of use
            </Link>{' '}
            and{' '}
            <Link to="/privacy-policy" style={{ color: '#0d6efd', textDecoration: 'none' }}>
              privacy policy
            </Link>.
          </p>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
          <div className="auth-separator">
            <span>Don't have an account?</span>
          </div>
          <Link to="/signup" className="auth-link-secondary">
            Get Started For Free
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;